import { useState } from "react";

import { useTranslation } from "react-i18next";

import Drawer from "../../layouts/Drawer";
import LoadingWheel from "./LoadingWheel";
import Avatar from "../user/Avatar";
import useAuditLogs, { IAuditLog } from "../../data/useAuditLogs";
import useWilmaDeletedUsers from "../../data/useWilmaDeletedUsers";
import formatDateTime from "../../utils/formatDateTime";
import { DateTime } from "luxon";

interface AuditLogDrawerProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityName: string;
  entityId: string;
}

function ChangeDetails({ changes }: { changes: string }) {
  const { t } = useTranslation("common");
  const parsed: Record<string, { Old?: string; New?: string }> = JSON.parse(changes);

  return (
    <div className="mt-2 space-y-1">
      {Object.entries(parsed).map(([prop, values]) => (
        <div key={prop} className="text-sm">
          <span className="font-medium text-gray-700">{prop}</span>
          <div className="ml-3 text-xs text-gray-500">
            <span className="line-through text-red-500">{values.Old || <em>{t("empty")}</em>}</span>
            {" → "}
            <span className="text-green-600">{values.New || <em>{t("empty")}</em>}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditEntry({ log, getUserName }: { log: IAuditLog; getUserName: (id: string) => string | undefined }) {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(false);
  const hasChanges = log.changes && log.action === "Updated";

  const actionColors: Record<string, string> = {
    Created: "bg-green-100 text-green-800",
    Updated: "bg-blue-100 text-blue-800",
    Deleted: "bg-red-100 text-red-800",
  };

  return (
    <div
      className={`border-b border-gray-100 py-3 ${hasChanges ? "cursor-pointer hover:bg-gray-50" : ""}`}
      onClick={() => hasChanges && setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${actionColors[log.action] || "bg-gray-100 text-gray-800"}`}>
            {t(log.action)}
          </span>
          {hasChanges && (
            <span className="text-xs text-gray-400">
              {expanded ? "▼" : "▶"} {t("{{count}} field(s)", { count: Object.keys(JSON.parse(log.changes!)).length })}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">
          {formatDateTime(log.timestamp, DateTime.DATETIME_MED_WITH_SECONDS)} 
        </span>
      </div>

      <div className="mt-1 flex items-center gap-1.5">
        {log.userId ? (
          <>
            <Avatar userId={log.userId} classes="w-5 h-5" textClasses="!text-[10px]" />
            <span className="text-xs text-gray-500">{getUserName(log.userId) || t("Unknown")}</span>
          </>
        ) : (
          <span className="text-xs text-gray-400 italic">{t("System")}</span>
        )}
      </div>

      {expanded && hasChanges && <ChangeDetails changes={log.changes!} />}
    </div>
  );
}

export default function AuditLogDrawer({ open, setOpen, entityName, entityId }: AuditLogDrawerProps) {
  const { t } = useTranslation("common");
  const { auditLogs } = useAuditLogs(entityName, entityId, "AuditLogDrawer");
  const { wilmaUsers } = useWilmaDeletedUsers();

  const getUserName = (userId: string) => {
    const user = wilmaUsers.data?.data?.find((u: any) => u.id === userId);
    return user ? `${user.forename} ${user.surname}` : undefined;
  };

  return (
    <Drawer open={open} setOpen={setOpen} title={t("Audit Log")} maxWidth="max-w-lg">
      {auditLogs.isLoading ? (
        <LoadingWheel />
      ) : auditLogs.data?.data?.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">{t("No audit records found.")}</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {auditLogs.data?.data?.map((log) => (
            <AuditEntry key={log.id} log={log} getUserName={getUserName} />
          ))}
        </div>
      )}
    </Drawer>
  );
}
